<template>
  <liste-template>
    <template #header>
      <h1 class="title is-size-3">{{ getTitre }}</h1>
      <breadcrumb :breadcrumb="breadcrumb"/>
    </template>
    <template #bar-left>
      <div class="column">
        <search-bar
          :nameStore="nameStore"
          :moduleDcf="moduleDcf"
          @recherche="recherche">
        </search-bar>
      </div>
    </template>
    <template #bar-right>
      <div class="columns">
        <div class="column">
          <exportExcelButton
            :perm="permissions.exporter"
            :query="exportExcelQuery"/>
        </div>
        <div class="column">
          <parametrage-table
            :referenceTable="referenceTable"
            @endLoadParamtre="startArrayRendering = true">
          </parametrage-table>
        </div>
      </div>
    </template>
    <template #content>
      <v-server-table
        :columns="columns"
        :options="options"
        @loaded="finLoading"
        @loading="debutLoading"
        :ref="referenceTable"
        v-if="startArrayRendering"
      >
        <div slot="dateMouvement" slot-scope="props">
          <router-link-blank :to="{ name: linkMouvementName,
            params: {id: props.row.remiseBancaireId}}"
            :message="func.formatDate(props.row.dateMouvement)">
          </router-link-blank>
        </div>
        <div slot="bailleur" slot-scope="props">
          <router-link-blank :to="{ name: linkFournisseurName,
            params: {id: props.row.tiersId}}"
            :message="props.row.tiers.nomCommercial">
          </router-link-blank>
        </div>
        <div slot="montantDuMouvement" slot-scope="props">
          <div
            v-if="props.row.montantDuMouvement !== undefined"
            class="is-pulled-right">
            {{func.formatDevise(props.row.montantDuMouvement)}}
          </div>
        </div>
        <div slot="montantTtc" slot-scope="props">
          <div
            v-if="props.row.montantTtc !== undefined"
            class="is-pulled-right">
            {{func.formatDevise(props.row.montantTtc)}}
          </div>
        </div>
        <div slot="montantLettre" slot-scope="props">
          <div
            v-if="props.row.montantLettre !== undefined"
            class="is-pulled-right">
            {{func.formatDevise(props.row.montantLettre)}}
          </div>
        </div>
        <div slot="codeFactureOuContrat" slot-scope="props">
          <div v-for="(codeFactureOuContrat, cIndex) in props.row.codeFactureOuContrat"
            :key="`codeFactureOuContrat-${codeFactureOuContrat.id}-${cIndex}`">
              <router-link-blank
                :to="{
                  name: linkCodeName(codeFactureOuContrat.isContrat),
                  params: {id: codeFactureOuContrat.id}}"
                :message="codeFactureOuContrat.code">
              </router-link-blank>
          </div>
        </div>
        <template slot='appendBody'>
          <table-footer-totaux
              ref="tableTotaux"
              :columns="columns"
              :target="targetTotal"
              :items="journalPaiements">
          </table-footer-totaux>
        </template>
        <template slot='afterTable'>
          <table-items-in-view
            :perPage="resultPerPage"
            @input="$refs[referenceTable].setLimit($event)"
          />
        </template>
      </v-server-table>
    </template>
  </liste-template>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import listeTemplate from '@/components/general/template/listeTemplate';
import Breadcrumb from '@/components/general/breadcrumb';
import TableItemsInView from '@/components/general/TableItemsInView';
import searchBar from '@/components/baux/journalPaiements/searchBar';
import {
  SORT_ICON,
  NO_PER_PAGE_VALUE,
  RESULTS_PER_PAGE,
  TEXTS,
} from '@/configuration/clientTableConf';
import parametrageTable from '@/components/general/parametrageTable';
import routerLinkBlank from '@/components/general/routerLinkBlank';
import formatDate from '@/services/general/formatDate';
import tableFooterTotaux from '@/components/general/tableFooterTotaux';
import messageErreur from '@/services/general/messageErreur';
import exportExcelButton from '@/components/general/button/exportExcelButton';
import { formatDevise } from '@/services/general/filters/formatDevise';
import {
  ECRANS_JOURNAL_PAIEMENTS_TYPE_EXPORT_EXCEL,
  ECRANS_JOURNAL_MOUVEMENTS_EXPORT_EXCEL,
} from '@/configuration/mouvementClient/mouvementClientConf';
import formatDateIso from '@/services/general/formatIsoDate';
import { MODULE_TLPE, MODULE_ACHAT } from '@/configuration/devisConf';

const permissions = createNamespacedHelpers('permissions');
const Societe = createNamespacedHelpers('societe');

export default {
  props: {
    referenceTable: {
      type: String,
      required: true,
    },
    permissions: {
      type: Object,
      required: true,
    },
    moduleDcf: {
      type: String,
      required: true,
    },
    nameStore: {
      type: String,
      required: false,
      default: 'journalPaiements',
    },
  },
  components: {
    searchBar,
    listeTemplate,
    Breadcrumb,
    parametrageTable,
    routerLinkBlank,
    tableFooterTotaux,
    TableItemsInView,
    exportExcelButton,
  },
  data() {
    return {
      resultPerPage: RESULTS_PER_PAGE,
      startArrayRendering: false,
      func: {
        formatDate,
        formatDevise,
        formatDateIso,
      },
    };
  },
  computed: {
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
    }),
    criteresRecherche() {
      return this.$store.getters[`${this.nameStore}/critereRechercheJournalPaiements`];
    },
    journalPaiements() {
      return this.$store.getters[`${this.nameStore}/items`];
    },
    total() {
      return this.$store.getters[`${this.nameStore}/total`];
    },
    lastPayload() {
      return this.$store.getters[`${this.nameStore}/lastPayload`];
    },
    targetTotal() {
      return ['montantTtc', 'montantDuMouvement', 'montantLettre'];
    },
    getTitre() {
      if (this.moduleDcf === MODULE_TLPE) return this.$t('journalPaiements.titreRecette');
      if (this.moduleDcf === MODULE_ACHAT) return this.$t('journalPaiements.titreFacture');
      return this.$t('journalPaiements.titre');
    },
    breadcrumb() {
      return [
        { title: this.$t('journalPaiements.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.getTitre, route: '', isActive: true },
      ];
    },
    getLabelFournisseur() {
      if (this.moduleDcf === MODULE_TLPE) return this.$t('journalPaiements.table.colonnes.collectivite');
      if (this.moduleDcf === MODULE_ACHAT) return this.$t('journalPaiements.table.colonnes.fournisseur');
      return this.$t('journalPaiements.table.colonnes.bailleur');
    },
    getLabelCode() {
      if (this.moduleDcf === MODULE_TLPE) return this.$t('journalPaiements.table.colonnes.codeTitreRecette');
      if (this.moduleDcf === MODULE_ACHAT) return this.$t('journalPaiements.table.colonnes.codeFactureAchat');
      return this.$t('journalPaiements.table.colonnes.codeFactureOuContrat');
    },
    getLabelCompteComptable() {
      if (this.moduleDcf === MODULE_TLPE) return this.$t('journalPaiements.table.colonnes.compteComptableCollectivite');
      if (this.moduleDcf === MODULE_ACHAT) return this.$t('journalPaiements.table.colonnes.compteComptableFournisseur');
      return this.$t('journalPaiements.table.colonnes.compteComptable');
    },
    columns() {
      return [
        'dateMouvement',
        'remiseBancaire.domiciliationBancaire.libelle',
        'ligneRemiseBancairePseudoEntity.emetteur',
        'ligneRemiseBancairePseudoEntity.referenceReglement',
        'bailleur',
        'tiers.compteComptable',
        'remiseBancaire.modeReglement.libelle',
        'montantDuMouvement',
        'codeFactureOuContrat',
        'montantTtc',
        'montantLettre',
      ];
    },
    options() {
      return {
        sortIcon: SORT_ICON,
        headings: {
          dateMouvement: this.$t('journalPaiements.table.colonnes.datePaiement'),
          'remiseBancaire.domiciliationBancaire.libelle': this.$t('journalPaiements.table.colonnes.domiciliationBancaire'),
          'ligneRemiseBancairePseudoEntity.referenceReglement': this.$t('journalPaiements.table.colonnes.referencePaiement'),
          'ligneRemiseBancairePseudoEntity.emetteur': this.$t('journalPaiements.table.colonnes.nomTire'),
          bailleur: this.getLabelFournisseur,
          'tiers.compteComptable': this.getLabelCompteComptable,
          'remiseBancaire.modeReglement.libelle': this.$t('journalPaiements.table.colonnes.modeReglement'),
          montantDuMouvement: this.$t('journalPaiements.table.colonnes.montantPaye'),
          codeFactureOuContrat: this.getLabelCode,
          montantTtc: this.$t('journalPaiements.table.colonnes.montantTtcARegle'),
          montantLettre: this.$t('journalPaiements.table.colonnes.montantLettre'),
        },
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: this.resultPerPage,
        texts: TEXTS,
        columnsClasses: {
          listeIntervenants: 'ellipsis colonneListeIntervenants',
        },
        filterable: false,
        sortable: this.columns.filter(c => c !== 'codeFactureOuContrat' && c !== 'montantTtc' && c !== 'montantLettre'),
        requestFunction: ((data) => {
          if (!data.query) data.query = cloneDeep(this.criteresRecherche);
          data.query.societeId = this.societeActiveId;
          data.query.tiersIds = data.query.tiersIds?.map(t => t.id);
          if (
            data.query.borneDebit !== null
            && data.query.borneDebit.borneMin === null
            && data.query.borneDebit.borneMax === null) {
            delete (data.query.borneDebit);
          }
          if (data.query.dates && data.query.dates.dateDebut) {
            data.query.dates.dateDebut = this.func.formatDateIso(data.query.dates.dateDebut);
          }
          if (data.query.dates && data.query.dates.dateFin) {
            data.query.dates.dateFin = this.func.formatDateIso(data.query.dates.dateFin);
          }
          if (data.orderBy?.includes('montantDuMouvement')) {
            data.orderBy = data.orderBy.replace('montantDuMouvement', 'montantDuPaiement');
          }
          return this.actionsLoadMouvement(data)
            .catch(() => {
              this.finLoading();
              messageErreur(this.$i18n);
            });
        }),
        responseAdapter: (() => {
          return {
            data: this.journalPaiements,
            count: this.total,
          };
        }),
      };
    },
    exportExcelQuery() {
      return {
        typeDonnee: ECRANS_JOURNAL_MOUVEMENTS_EXPORT_EXCEL,
        typeExport: ECRANS_JOURNAL_PAIEMENTS_TYPE_EXPORT_EXCEL,
        filtres: {
          ...this.lastPayload,
          ...(this.lastPayload.orderBy
            && { sorting: `${this.lastPayload.orderBy.split(' ')[0]} ${this.lastPayload.ascending ? ' ASC' : ' DESC'}` }),
        },
        parametresAdditionnels: {
          moduleDcf: this.moduleDcf,
        },
      };
    },
    linkMouvementName() {
      if (this.moduleDcf === MODULE_TLPE) return 'c.gabed.lot_paiement_recette';
      if (this.moduleDcf === MODULE_ACHAT) return 'c.achat.lot_paiements_factures';
      return 'c.baux.lot_paiement';
    },
    linkFournisseurName() {
      if (this.moduleDcf === MODULE_TLPE) return 'c.gabed.collectivite';
      if (this.moduleDcf === MODULE_ACHAT) return 'c.achat.fournisseurs';
      return 'c.baux.bailleur';
    },
  },
  methods: {
    ...permissions.mapActions({
      showPopUp: 'showPopup',
    }),
    actionsLoadMouvement(data) {
      return this.$store.dispatch(`${this.nameStore}/loadJournalPaiements`, data);
    },
    recherche() {
      this.$refs[this.referenceTable].refresh();
    },
    debutLoading() {
      if (this.loadingComponent) this.loadingComponent.close();
      this.loadingComponent = this.$buefy.loading.open();
    },
    finLoading() {
      this.loadingComponent.close();
    },
    linkCodeName(isContrat) {
      if (this.moduleDcf === MODULE_TLPE) return 'c.gabed.titre_recette';
      if (this.moduleDcf === MODULE_ACHAT) return 'c.achat.factures';
      return isContrat ? 'c.baux.contrat' : 'c.baux.facture_achat';
    },
  },
};
</script>
